import type { OfferWithRelations } from "./offer-types"
import {
  isBuyerResponseTurn,
  isSellerResponseTurn,
} from "./offer-response-turn"

export type OfferTab = "received" | "sent"
export type OffersChipFilter = "all" | "action" | "waiting" | "accepted" | "past"
export type OfferSortKey = "expiring_soon" | "price_low" | "price_high"

export type OfferChipCounts = {
  all: number
  action: number
  waiting: number
  accepted: number
  past: number
}

export function depthFromOfferRoot(
  o: OfferWithRelations,
  byId: Map<string, OfferWithRelations>,
): number | null {
  const seen = new Set<string>()
  const chain: OfferWithRelations[] = []
  let cur: OfferWithRelations | undefined = o
  while (cur) {
    if (seen.has(cur.id)) return null
    seen.add(cur.id)
    chain.push(cur)
    if (!cur.parent_offer_id) break
    const p = byId.get(cur.parent_offer_id)
    if (!p) return null
    cur = p
  }
  return chain.length - 1
}

export function withoutSupersededCounterParents(
  offers: OfferWithRelations[],
): OfferWithRelations[] {
  const parentIds = new Set<string>()
  for (const x of offers) {
    if (x.parent_offer_id) parentIds.add(x.parent_offer_id)
  }
  return offers.filter(
    (x) => !(x.status === "counter_offered" && parentIds.has(x.id)),
  )
}

export function matchesOfferChip(
  offer: OfferWithRelations,
  tab: OfferTab,
  chip: OffersChipFilter,
  offerMap: Map<string, OfferWithRelations>,
): boolean {
  switch (chip) {
    case "all":
      return true
    case "action":
      return tab === "received"
        ? isSellerResponseTurn(offer, offerMap)
        : isBuyerResponseTurn(offer, offerMap)
    case "waiting":
      return (
        offer.status === "pending" &&
        (tab === "received"
          ? !isSellerResponseTurn(offer, offerMap)
          : !isBuyerResponseTurn(offer, offerMap))
      )
    case "accepted":
      return offer.status === "accepted" && offer.settled_at == null
    case "past":
      return (
        offer.status === "declined" ||
        offer.status === "expired" ||
        offer.status === "withdrawn" ||
        (offer.status === "accepted" && offer.settled_at != null)
      )
    default:
      return true
  }
}

export function offerDeadlineSortKey(o: OfferWithRelations): number {
  const awaiting = o.status === "accepted" && o.settled_at == null
  const iso = awaiting && o.payment_deadline ? o.payment_deadline : o.expires_at
  const ms = new Date(iso).getTime()
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY
}

export function compareOffersBySort(a: OfferWithRelations, b: OfferWithRelations, sort: OfferSortKey): number {
  switch (sort) {
    case "price_low":
      return a.amount - b.amount
    case "price_high":
      return b.amount - a.amount
    case "expiring_soon":
    default:
      return offerDeadlineSortKey(a) - offerDeadlineSortKey(b)
  }
}

export function compareOffersByRecency(a: OfferWithRelations, b: OfferWithRelations): number {
  const aMs = new Date(a.updated_at).getTime()
  const bMs = new Date(b.updated_at).getTime()
  const aValid = Number.isFinite(aMs)
  const bValid = Number.isFinite(bMs)
  if (!aValid && !bValid) return 0
  if (!aValid) return 1
  if (!bValid) return -1
  return bMs - aMs
}

export function computeOfferChipCounts(
  offers: OfferWithRelations[],
  tab: OfferTab,
  offerMap: Map<string, OfferWithRelations>,
): OfferChipCounts {
  let action = 0
  let waiting = 0
  let accepted = 0
  let past = 0
  for (const o of offers) {
    if (matchesOfferChip(o, tab, "action", offerMap)) action++
    if (matchesOfferChip(o, tab, "waiting", offerMap)) waiting++
    if (matchesOfferChip(o, tab, "accepted", offerMap)) accepted++
    if (matchesOfferChip(o, tab, "past", offerMap)) past++
  }
  return { all: offers.length, action, waiting, accepted, past }
}

export function discountPct(amount: number, listPrice: number | null): number | null {
  if (listPrice == null || listPrice <= 0) return null
  if (amount >= listPrice) return null
  return Math.round((1 - amount / listPrice) * 100)
}

export function offerCountdownShort(iso: string, nowMs = Date.now()): string {
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return ""
  const ms = Math.max(0, t - nowMs)
  const mins = Math.floor(ms / 60000)
  if (mins >= 1440) return `${Math.floor(mins / 1440)}d`
  if (mins >= 60) return `${Math.floor(mins / 60)}h`
  return `${Math.max(1, mins)}m`
}
